import { useEffect, useMemo, useState } from 'react'
import { useMediaQuery } from 'react-responsive'
import { useHints } from '#app/utils/client-hints.js'
import { breakpoints as breakpointDefs } from '#app/utils/tailwind.util.js'

function getBreakpointDefs() {
  return Object.keys(breakpointDefs).reduce(
    (acc, key: string) => {
      acc[key] = parseInt(
        breakpointDefs[key as keyof typeof breakpointDefs].replace('px', ''),
      )
      return acc
    },
    {} as Record<string, number>,
  )
}

function useInitialBreakpoints() {
  const width = useHints().innerWidth
  const bpDefs = getBreakpointDefs()

  return useMemo(
    () => ({
      isXs: width >= bpDefs['xs'],
      isXsToSm: width >= bpDefs['xs'] && width < bpDefs['sm'],
      isSm: width >= bpDefs['sm'],
      isSmToMd: width >= bpDefs['sm'] && width < bpDefs['md'],
      isMd: width >= bpDefs['md'],
      isMdToLg: width >= bpDefs['md'] && width < bpDefs['lg'],
      isLg: width >= bpDefs['lg'],
      isLgToXl: width >= bpDefs['lg'] && width < bpDefs['xl'],
      isXl: width >= bpDefs['xl'],
      isXlTo2xl: width >= bpDefs['xl'] && width < bpDefs['2xl'],
      is2xl: width >= bpDefs['2xl'],
      is2xlTo3xl: width >= bpDefs['2xl'] && width < bpDefs['3xl'],
      is3xl: width >= bpDefs['3xl'],
      is3xlTo4xl: width >= bpDefs['3xl'] && width < bpDefs['4xl'],
      is4xl: width >= bpDefs['4xl'],
    }),
    [], // This is run once. During SSR.
  )
}

function useBreakpointCalc() {
  const bpDefs = getBreakpointDefs()
  const isXs = useMediaQuery({ minWidth: bpDefs['xs'] })
  const isXsToSm = useMediaQuery({
    minWidth: bpDefs['xs'],
    maxWidth: bpDefs['sm'],
  })
  const isSm = useMediaQuery({ minWidth: bpDefs['sm'] })
  const isSmToMd = useMediaQuery({
    minWidth: bpDefs['sm'],
    maxWidth: bpDefs['md'],
  })
  const isMd = useMediaQuery({ minWidth: bpDefs['md'] })
  const isMdToLg = useMediaQuery({
    minWidth: bpDefs['md'],
    maxWidth: bpDefs['lg'],
  })
  const isLg = useMediaQuery({ minWidth: bpDefs['lg'] })
  const isLgToXl = useMediaQuery({
    minWidth: bpDefs['lg'],
    maxWidth: bpDefs['xl'],
  })
  const isXl = useMediaQuery({ minWidth: bpDefs['xl'] })
  const isXlTo2xl = useMediaQuery({
    minWidth: bpDefs['xl'],
    maxWidth: bpDefs['2xl'],
  })
  const is2xl = useMediaQuery({ minWidth: bpDefs['2xl'] })
  const is2xlTo3xl = useMediaQuery({
    minWidth: bpDefs['2xl'],
    maxWidth: bpDefs['3xl'],
  })
  const is3xl = useMediaQuery({ minWidth: bpDefs['3xl'] })
  const is3xlTo4xl = useMediaQuery({
    minWidth: bpDefs['3xl'],
    maxWidth: bpDefs['4xl'],
  })
  const is4xl = useMediaQuery({ minWidth: bpDefs['4xl'] })

  const breakpoints = useMemo(
    () => ({
      isXs,
      isXsToSm,
      isSm,
      isSmToMd,
      isMd,
      isMdToLg,
      isLg,
      isLgToXl,
      isXl,
      isXlTo2xl,
      is2xl,
      is2xlTo3xl,
      is3xl,
      is3xlTo4xl,
      is4xl,
    }),
    [
      isXs,
      isXsToSm,
      isSm,
      isSmToMd,
      isMd,
      isMdToLg,
      isLg,
      isLgToXl,
      isXl,
      isXlTo2xl,
      is2xl,
      is2xlTo3xl,
      is3xl,
      is3xlTo4xl,
      is4xl,
    ],
  )

  return breakpoints
}

export function useBreakpoints() {
  const initialBreakpoints = useInitialBreakpoints()
  const clientSideBreakpoints = useBreakpointCalc()
  const [breakpoints, setBreakpoints] = useState(initialBreakpoints)

  useEffect(() => {
    setBreakpoints(clientSideBreakpoints)
  }, [clientSideBreakpoints])

  return breakpoints
}
